'use client';

import { Canvas } from '@react-three/fiber';
import React, { Suspense, useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import { BerryModel } from './BerryModel';
import type { CompanionState } from './motion';
import { cn } from '@core/lib';
import { useReducedMotion } from 'framer-motion';

export type { CompanionState } from './motion';

class ModelBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: Error) {
    console.warn('[Companion] Using portrait fallback:', error.message);
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

const descriptions: Record<CompanionState, string> = {
  idle: 'Ready for a question or recording',
  attentive: 'Following your message',
  listening: 'Listening to your recording',
  paused: 'Microphone is off',
  thinking: 'Preparing your response',
  responding: 'Writing your response',
  ready: 'Your response is ready',
};

export function ScribeberryCompanion({
  still = false,
  size = 40,
  state = 'idle',
  lookYaw = null,
  lookPitch = null,
  className,
}: {
  still?: boolean;
  size?: number;
  state?: CompanionState;
  lookYaw?: number | null;
  lookPitch?: number | null;
  className?: string;
}) {
  const reducedMotion = useReducedMotion();
  const [foreground, setForeground] = useState(true);
  const [ready, setReady] = useState(false);
  const onReady = useCallback(() => setReady(true), []);

  useEffect(() => {
    const onVis = () => setForeground(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const working = state === 'thinking' || state === 'responding';
  const portrait = working
    ? '/models/portrait-writing.png'
    : '/models/portrait.png';
  const animate = foreground && !still && !reducedMotion;

  const fallback = (
    <Image
      src={portrait}
      alt=''
      fill
      className='object-contain'
      sizes={`${size}px`}
      priority
    />
  );

  return (
    <div
      aria-hidden
      className={cn(
        'relative shrink-0 overflow-hidden rounded-full border border-border/60 bg-card',
        className,
      )}
      style={{ width: size, height: size }}
      title={descriptions[state]}
    >
      {!ready && fallback}
      <ModelBoundary fallback={fallback}>
        <Suspense fallback={null}>
          <Canvas
            frameloop='demand'
            orthographic
            flat
            gl={{ alpha: true, antialias: true }}
            camera={{
              position: [0.22, 2.76, 8],
              zoom: size / 1.55,
              near: 0.1,
              far: 30,
            }}
            onCreated={({ camera, gl }) => {
              camera.lookAt(0, 1.66, 0);
              gl.setClearColor(0x000000, 0);
            }}
            style={{ width: size, height: size }}
          >
            <hemisphereLight args={['#fff5ea', '#665768', 1.05]} />
            <directionalLight
              position={[-3, 5, 4]}
              intensity={2.4}
              color='#fff4e8'
            />
            <directionalLight
              position={[3, 2, 2]}
              intensity={0.5}
              color='#f0f6ff'
            />
            <directionalLight
              position={[1, 3, -3]}
              intensity={1.8}
              color='#e6fff7'
            />
            <BerryModel
              onReady={onReady}
              activity={{
                state,
                greeting: 0,
                animate,
                lookYaw,
                lookPitch,
              }}
            />
          </Canvas>
        </Suspense>
      </ModelBoundary>
    </div>
  );
}
